import { RebateOverview } from "@/gql/graphql";

/**
 * The goal helpers below only compare target values and hand the chosen goal
 * straight back, so they are typed on the shape they touch rather than on
 * RebateOverview. That way the bookings view, whose query selects a narrower
 * row, can use them without pretending to be a full overview.
 */
interface GoalWithTarget {
  id: string;
  targetValue: number;
}

interface RebateWithGoals<TGoal extends GoalWithTarget> {
  achieved: number;
  goals: TGoal[];
}

export const getActiveGoalId = <TGoal extends GoalWithTarget>(
  rebate: RebateWithGoals<TGoal> | undefined
) => getActiveGoal(rebate)?.id;

export const getExpectedGoal = (rebate: RebateOverview | undefined) =>
  rebate
    ? rebate.goals
        .filter((goal) => goal.targetValue <= goal.expectedVolume)
        .sort((a, b) => b.targetValue - a.targetValue)
        .at(0)
    : null;

export const getActiveGoal = <TGoal extends GoalWithTarget>(
  rebate: RebateWithGoals<TGoal> | undefined
) => {
  if (!rebate) {
    return null;
  }
  const achievedGoal = getAchievedGoal(rebate);
  if (achievedGoal) {
    return achievedGoal;
  }

  return [...rebate.goals].sort((a, b) => a.targetValue - b.targetValue).at(0);
};
export const getAchievedGoal = <TGoal extends GoalWithTarget>(
  rebate: RebateWithGoals<TGoal> | undefined
) => {
  if (!rebate) {
    return null;
  }
  const reachedGoal = rebate.goals
    .filter((goal) => goal.targetValue <= rebate.achieved)
    .sort((a, b) => b.targetValue - a.targetValue)
    .at(0);

  return reachedGoal;
};

export const getAuthorizedBy = (rebate: RebateOverview | undefined) =>
  getExpectedGoal(rebate)?.authorizedBy?.replace("LINE\\", "");

const joinDistinct = (values: Array<string | null | undefined>) =>
  [...new Set(values.filter((value): value is string => !!value))].join(", ");

export const getCriteriaNames = (
  rebate: { criterias: Array<{ name: string | null }> } | undefined
) => joinDistinct(rebate?.criterias.map((criteria) => criteria.name) ?? []);

export const getCriteriaDescriptions = (
  rebate: { criterias: Array<{ description: string | null }> } | undefined
) =>
  joinDistinct(rebate?.criterias.map((criteria) => criteria.description) ?? []);

export const getRebateModelCode = (
  rebate: { criterias: Array<{ rebateModelCode: string | null }> } | undefined
) => rebate?.criterias.at(0)?.rebateModelCode;

/**
 * The bookings view is opened from a single row in Handle rebates, and that row
 * is one goal grouping on the agreement. The overview query can only filter on
 * the agreement number, so narrow the result to the row holding the selected
 * goals and drop the rest - otherwise the view shows bookings from periods the
 * user never asked for.
 *
 * Without goal ids there is nothing to show: picking a row on our own is how the
 * view used to land on the wrong period.
 */
export const selectRebateOverviewForGoals = <
  TOverview extends { goals: Array<{ id: string }> }
>(
  rebateOverview: TOverview[],
  goalIds: string[]
): TOverview | undefined => {
  if (goalIds.length === 0) {
    return undefined;
  }

  const selectedGoalIds = new Set(goalIds);

  for (const overview of rebateOverview) {
    const goals = overview.goals.filter((goal) => selectedGoalIds.has(goal.id));

    if (goals.length > 0) {
      return { ...overview, goals };
    }
  }

  return undefined;
};
